import React, { useMemo, useState } from 'react'
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, functions } from '../firebase-config'

function fmt(ts) {
  const d = ts?.toDate ? ts.toDate() : new Date(ts)
  if (!d || Number.isNaN(d.getTime())) return '-'
  return d.toLocaleString('es-AR')
}

export default function TicketConversation({ ticket, compact = false }) {
  const [messages, setMessages] = useState([])
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState(null)

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 2500)
  }

  React.useEffect(() => {
    if (!ticket?.id) {
      setMessages([])
      return undefined
    }
    const q = query(
      collection(db, 'tickets', ticket.id, 'ticket_messages'),
      orderBy('createdAt', 'asc')
    )
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    })
    return () => unsub()
  }, [ticket?.id])

  const discrepancia = useMemo(() => {
    const motor = import.meta.env.VITE_MOTOR_HASH || 'N/A'
    const ticketNorma = ticket?.versionNormativa || 'desconocida'
    return motor !== 'N/A' && ticketNorma !== 'desconocida' && ticketNorma !== motor
  }, [ticket?.versionNormativa])

  const send = async () => {
    if (ticket?.estado === 'resuelto') return
    if (!ticket?.id || text.trim().length < 2) return
    setBusy(true)
    showToast('Enviando respuesta...', 'info')
    try {
      const callAdd = httpsCallable(functions, 'addTicketMessage')
      await callAdd({ ticketId: ticket.id, body: text.trim() })
      setText('')
      showToast('Respuesta registrada.', 'success')
    } catch (e) {
      showToast(e?.message || 'No se pudo enviar la respuesta.', 'error')
    } finally {
      setBusy(false)
    }
  }

  if (!ticket?.id) return null

  return (
    <div className={`rounded-xl border border-slate-200 bg-white ${compact ? 'p-3' : 'p-4'} shadow-sm`}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{ticket.ticketCode || ticket.id}</span>
        <span className="rounded bg-cyan-100 px-2 py-0.5 text-xs text-cyan-800">{ticket.estado || 'abierto'}</span>
      </div>
      {discrepancia && (
        <p className="mb-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800">
          Alerta de discrepancia normativa: ticket {ticket.versionNormativa || 'N/A'} vs motor actual {import.meta.env.VITE_MOTOR_HASH || 'N/A'}.
        </p>
      )}
      <div className="max-h-56 space-y-2 overflow-auto rounded border border-slate-200 bg-slate-50 p-2">
        {messages.map((m) => (
          <div key={m.id} className={`rounded px-2 py-1 text-xs ${m.authorRole === 'admin' ? 'bg-blue-100 text-blue-900' : 'bg-white text-slate-800 border border-slate-200'}`}>
            <p className="font-semibold">{m.authorRole === 'admin' ? 'Soporte' : 'Usuario'} · {fmt(m.createdAt)}</p>
            <p className="break-words">{m.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Escribir respuesta"
          className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 placeholder:text-slate-500"
          disabled={ticket?.estado === 'resuelto'}
        />
        <button type="button" onClick={send} disabled={busy || ticket?.estado === 'resuelto'} className="rounded bg-cyan-600 px-3 py-1 text-xs font-semibold text-white disabled:bg-slate-400">Enviar</button>
      </div>
      {ticket?.estado === 'resuelto' && <p className="mt-2 text-xs text-amber-200">Este ticket esta cerrado y no admite nuevos mensajes.</p>}
      {toast && (
        <p className={`mt-2 text-xs font-semibold ${toast.type === 'error' ? 'text-rose-700' : toast.type === 'info' ? 'text-slate-600' : 'text-emerald-700'}`}>
          {toast.message}
        </p>
      )}
    </div>
  )
}
